/**
 * Outbox — Rule O-02: attendance (and other write-heavy flows) queue while
 * offline and drain when the network returns. Storage: `localStorage`. Small
 * enough to fit; simple enough to survive a page reload; no schema migration.
 *
 * Callers own the shape of `payload`. The outbox is agnostic to what's being
 * queued — it hands the payload back on drain, and the caller re-POSTs it.
 */

export const SYNC_VERSION = '0.0.2';

const KEY = 'velora.outbox.v1';

export interface OutboxItem<P = unknown> {
  id: string;
  op: string; // e.g. 'attendance.mark' — caller-defined
  payload: P;
  created_at: string;
  attempts: number;
  last_error?: string | null;
}

function read(): OutboxItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as OutboxItem[]) : [];
  } catch {
    return [];
  }
}

function write(items: OutboxItem[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(items));
    window.dispatchEvent(new CustomEvent('velora:outbox-change'));
  } catch {
    /* quota errors are silent — the caller sees a bigger list next tick */
  }
}

export function outboxList(): OutboxItem[] {
  return read();
}

export function outboxCount(): number {
  return read().length;
}

export function outboxEnqueue<P>(op: string, payload: P): OutboxItem<P> {
  const item: OutboxItem<P> = {
    id: (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
      ? crypto.randomUUID()
      : `q_${Date.now()}_${Math.random().toString(16).slice(2)}`,
    op,
    payload,
    created_at: new Date().toISOString(),
    attempts: 0,
    last_error: null,
  };
  write([...read(), item as OutboxItem]);
  return item;
}

export function outboxRemove(id: string): void {
  write(read().filter((i) => i.id !== id));
}

export function outboxMarkFailed(id: string, err: string): void {
  const list = read();
  const idx = list.findIndex((i) => i.id === id);
  if (idx < 0) return;
  const item = list[idx];
  if (!item) return;
  list[idx] = { ...item, attempts: item.attempts + 1, last_error: err };
  write(list);
}

/**
 * Drain: iterate items and try to send each via the provided sender.
 * Successful items are removed; failures stay in the outbox with attempts++.
 * Stops on the first failure to preserve order (attendance is order-sensitive).
 */
export async function outboxDrain<P>(
  op: string,
  send: (payload: P) => Promise<void>,
): Promise<{ sent: number; failed: number }> {
  const list = read().filter((i) => i.op === op);
  let sent = 0;
  let failed = 0;
  for (const item of list) {
    try {
      await send(item.payload as P);
      outboxRemove(item.id);
      sent += 1;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      outboxMarkFailed(item.id, msg);
      failed += 1;
      break; // preserve order
    }
  }
  return { sent, failed };
}

/** Subscribe to changes — useful for badges. Returns unsubscribe fn. */
export function outboxSubscribe(fn: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener('velora:outbox-change', fn);
  window.addEventListener('storage', fn);
  return () => {
    window.removeEventListener('velora:outbox-change', fn);
    window.removeEventListener('storage', fn);
  };
}
